import { Suspense, useEffect, useRef, useState, Component } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Center, Bounds, Html, useGLTF, Environment, Lightformer, Text } from "@react-three/drei";
import * as THREE from "three";
import { assetUrl } from "@/lib/api";
import { productFonts } from "@/lib/fonts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cube, Check, NotePencil, Ruler, ArrowsClockwise, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

function Model({ url, finish, onSize }) {
  const { scene } = useGLTF(url);
  useEffect(() => {
    scene.traverse((o) => {
      if (o.isMesh && o.material) {
        const m = o.material;
        m.color = new THREE.Color(finish.color);
        m.metalness = finish.metalness;
        m.roughness = finish.roughness;
        m.map = null;
        m.needsUpdate = true;
      }
    });
  }, [scene, finish]);
  useEffect(() => {
    const box = new THREE.Box3().setFromObject(scene);
    const s = new THREE.Vector3(); box.getSize(s);
    onSize?.({ x: s.x, y: s.y, z: s.z });
  }, [scene, onSize]);
  return <primitive object={scene} />;
}

class Boundary extends Component {
  constructor(p) { super(p); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  render() { return this.state.err ? <Html center><div style={{ color: "#999", fontSize: 13 }}>No se pudo cargar el modelo 3D.</div></Html> : this.props.children; }
}

export default function ThreeViewer({ product }) {
  const url = assetUrl(product.model_3d_url);
  const metals = product.metals?.length ? product.metals : [{ id: "silver", name: "Plata", hex: "#C0C0C0", price: 0 }];
  const [finish, setFinish] = useState({ color: metals[0].hex, metalness: 0.85, roughness: 0.3, name: metals[0].name, price: metals[0].price || 0 });
  const fonts = productFonts(product);
  const [font, setFont] = useState(fonts[0]);
  const [engraving, setEngraving] = useState("");
  const [ringSize, setRingSize] = useState("");
  const [autoRotate, setAutoRotate] = useState(true);
  const [size, setSize] = useState(null);
  const glRef = useRef(null);
  const price = finish.price > 0 ? finish.price : (product.price || 0);

  const snapshot = () => { try { return glRef.current?.domElement?.toDataURL("image/png"); } catch { return null; } };

  const addToWixCart = () => {
    const summary = [`Acabado: ${finish.name}`, engraving && `Grabado: "${engraving}"`, ringSize && `Talla: ${ringSize}`].filter(Boolean).join(" · ");
    window.parent.postMessage({
      type: "ADD_TO_WIX_CART",
      payload: { productName: product.name, wixProductId: product.wix_product_id || null, appId: product.wix_stores_app_id,
        quantity: 1, price, customizationText: summary, renderImageUrl: snapshot() },
    }, product.wix_site_origin || "*");
    toast.success("Diseño enviado al carrito de Wix");
  };

  return (
    <div className="min-h-screen bg-[#f6f5f2] text-obsidian" data-testid="three-viewer">
      <div className="grid lg:grid-cols-2 min-h-screen">
        <div className="relative bg-gradient-to-b from-neutral-100 to-neutral-300" data-testid="three-canvas-wrap">
          <Canvas camera={{ position: [0, 0, 3.4], fov: 45 }} gl={{ preserveDrawingBuffer: true, antialias: true }}
            onCreated={({ gl }) => { glRef.current = gl; }} style={{ width: "100%", height: "100%", minHeight: "60vh" }}>
            <color attach="background" args={["#e9e7e3"]} />
            <ambientLight intensity={0.5} />
            <hemisphereLight intensity={0.5} groundColor="#c8c8c8" />
            <directionalLight position={[5, 8, 5]} intensity={1.6} />
            <directionalLight position={[-6, 3, -4]} intensity={0.9} />
            <Environment resolution={256} frames={1}>
              <color attach="background" args={["#1a1a22"]} />
              <Lightformer form="rect" intensity={4} position={[0, 5, -6]} scale={[12, 7, 1]} />
              <Lightformer form="rect" intensity={3} position={[-6, 1, 4]} scale={[9, 7, 1]} />
              <Lightformer form="rect" intensity={2.5} position={[6, 2, 3]} scale={[7, 7, 1]} />
              <Lightformer form="ring" intensity={2} position={[0, -4, 4]} scale={5} />
            </Environment>
            <Suspense fallback={<Html center><CircleNotch className="animate-spin" size={26} /></Html>}>
              <Boundary>
                <Bounds fit clip observe margin={1.15}>
                  <group>
                    <Center><Model url={url} finish={finish} onSize={setSize} /></Center>
                    {engraving && size && product.engraving_project_3d && (
                      <Text position={[0, -size.y * 0.24, size.z * 0.52]} fontSize={Math.max(size.y * 0.05, 0.001)}
                        font={font?.url} color="#222" anchorX="center" anchorY="middle" maxWidth={size.x * 0.85}
                        outlineWidth={size.y * 0.0015} outlineColor="#000">
                        {engraving}
                      </Text>
                    )}
                  </group>
                </Bounds>
              </Boundary>
            </Suspense>
            <OrbitControls makeDefault enablePan={false} autoRotate={autoRotate} autoRotateSpeed={1.6} minDistance={1} maxDistance={40} />
          </Canvas>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs bg-obsidian/70 text-white px-3 py-1 rounded-full flex items-center gap-1">
            <ArrowsClockwise size={13} /> Arrastra para girar · rueda para zoom
          </div>
        </div>

        <div className="p-8 lg:p-12 space-y-7 overflow-y-auto">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold flex items-center gap-1"><Cube size={14} /> Vista 3D interactiva</span>
            <h2 className="font-serif text-3xl tracking-tight mt-1">{product.name}</h2>
            {product.description && <p className="text-sm text-neutral-500 mt-1">{product.description}</p>}
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">Metal / acabado</div>
            <div className="flex flex-wrap gap-2">
              {metals.map((m) => (
                <button key={m.id} onClick={() => setFinish((f) => ({ ...f, color: m.hex, name: m.name, price: m.price || 0 }))} data-testid={`three-metal-${m.id}`}
                  className={`px-3 py-1.5 rounded-full border text-sm flex items-center gap-1.5 ${finish.name === m.name ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}>
                  <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ background: m.hex }} /> {m.name}
                  {finish.name === m.name && <Check size={13} className="text-gold" />}
                </button>
              ))}
              <label className="flex items-center gap-1.5 px-2 py-1.5 rounded-full border border-neutral-300 bg-white text-sm cursor-pointer">
                <input type="color" value={finish.color} onChange={(e) => setFinish((f) => ({ ...f, color: e.target.value, name: "Personalizado" }))} className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent" data-testid="three-color-custom" />
                Otro
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-xs text-neutral-500 mb-1">Brillo metálico</div>
                <input type="range" min="0" max="1" step="0.05" value={finish.metalness} onChange={(e) => setFinish((f) => ({ ...f, metalness: parseFloat(e.target.value) }))} className="w-full" data-testid="three-metalness" />
              </div>
              <div>
                <div className="text-xs text-neutral-500 mb-1">Pulido</div>
                <input type="range" min="0" max="1" step="0.05" value={1 - finish.roughness} onChange={(e) => setFinish((f) => ({ ...f, roughness: 1 - parseFloat(e.target.value) }))} className="w-full" data-testid="three-roughness" />
              </div>
            </div>
          </div>

          {product.engraving_enabled && (
            <div>
              <div className="text-sm font-semibold mb-1 flex items-center gap-2"><NotePencil size={16} className="text-gold" /> Grabado</div>
              <Input value={engraving} maxLength={product.engraving_max_length || 20} onChange={(e) => setEngraving(e.target.value)} placeholder="Texto a grabar" className="bg-white border-neutral-300" data-testid="three-engraving-input" />
              {fonts.length > 1 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {fonts.map((f) => (
                    <button key={f.id} onClick={() => setFont(f)} data-testid={`three-font-${f.id}`}
                      className={`px-3 py-1.5 rounded-full border text-sm ${font?.id === f.id ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}
                      style={{ fontFamily: f.css }}>{engraving || "Abc"}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {product.size_enabled && (
            <div>
              <div className="text-sm font-semibold mb-1 flex items-center gap-2"><Ruler size={16} className="text-gold" /> Talla</div>
              <Select value={ringSize} onValueChange={setRingSize}>
                <SelectTrigger className="bg-white border-neutral-300" data-testid="three-size-select"><SelectValue placeholder="Elige talla" /></SelectTrigger>
                <SelectContent>{(product.size_options || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
            <input type="checkbox" checked={autoRotate} onChange={(e) => setAutoRotate(e.target.checked)} data-testid="three-autorotate" /> Rotación automática
          </label>

          <div className="border-t border-neutral-200 pt-5 flex items-center justify-between">
            <div><div className="text-xs text-neutral-500">Precio</div><div className="text-2xl font-serif">${price.toFixed(2)}</div></div>
            {product.wix_site_origin && (
              <Button onClick={addToWixCart} className="rounded-full bg-obsidian hover:bg-obsidian/90 text-white font-semibold" data-testid="three-wix-cart-btn">Enviar al carrito de Wix</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
